import type { DataTableColumn, FiltersMessages, ToolItemActionPermissions } from '../../models'
import type { DomainServiceProvider } from '../../providers/interfaces/domain-service.provider'
import type {
  IToolingBaseComposableDataItem,
  IToolingBaseComposableDataItemCriteria,
} from '../use-tooling-base/types'
import type { IToolingListComposableComputed, IToolingListComposableViewData } from './types'
// @ts-strict-ignore
import { DomainObjectUtils } from '@empathyco/playboard-core-domain'
import { computed, getCurrentInstance } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FilterIdEnum } from '../../enums/filter-id.enum'
import { ToolItemAction } from '../../models'
import { FiltersUtils } from '../../utils'
import { computedFn as toolingBaseComputedFn } from '../use-tooling-base/computed'
import { sortValuesAlphabetically } from './utils'

export const computedFn = <DataItemType extends IToolingBaseComposableDataItem>(
  viewData?: Partial<IToolingListComposableViewData>,
): IToolingListComposableComputed<DataItemType> => {
  const {
    proxy: { $epb },
  } = getCurrentInstance()

  const toolingBaseComputed = toolingBaseComputedFn<DataItemType>()
  const domainService = CONTAINER.get<DomainServiceProvider<DataItemType>>(
    TYPES.DomainServiceProvider,
  )(toolingBaseComputed.featureId.value)
  const { visiblePivotsConfig, visibleFiltersConfig, data } = toolingBaseComputed
  const filtersMessages: FiltersMessages = $epb.messages.filters

  const filtersConfig = computed(() => {
    return viewData?.useVisibleFiltersAsPivotColumns
      ? Object.values(visibleFiltersConfig.value).filter(
          filter => filter.id !== FilterIdEnum.ACTIVE,
        )
      : Object.values(visiblePivotsConfig.value)
  })

  const visiblePivotColumns = computed((): Array<DataTableColumn> => {
    return filtersConfig.value.map(config => ({
      id: `criteria.pivots.${config.id}`,
      tooltipId: `criteria.pivotsTooltip.${config.id}`,
      text: FiltersUtils.getLabel(config, filtersMessages),
      unwrapText: true,
    }))
  })

  const getActionPermissions = (item: DataItemType): ToolItemActionPermissions => {
    const hasEditPermission = domainService.hasEditPermission
      ? domainService.hasEditPermission(item)
      : true
    return {
      edit: hasEditPermission,
      copy: hasEditPermission,
      delete: hasEditPermission,
      enable: hasEditPermission && !item.criteria.enabled,
      disable: hasEditPermission && item.criteria.enabled,
      ...(domainService.getActionPermissions ? domainService.getActionPermissions(item) : {}),
    }
  }

  const getDataItemOptions = (dataItemType: DataItemType): Array<any> => {
    const actionPermissions = getActionPermissions(dataItemType)
    const showEnableActions = viewData?.enableActionsAvailable ?? true
    return [
      {
        id: ToolItemAction.Edit,
        text: $epb.messages.crud.actions.update,
        disabled: !actionPermissions.edit,
      },
      {
        id: ToolItemAction.Copy,
        text: $epb.messages.crud.actions.clone,
        disabled: !actionPermissions.copy,
      },
      {
        id: ToolItemAction.Delete,
        text: $epb.messages.crud.actions.delete,
        disabled: !actionPermissions.delete,
      },
      ...(showEnableActions
        ? [
            {
              id: ToolItemAction.Enable,
              text: $epb.messages.crud.actions.enable,
              disabled: !actionPermissions.enable,
            },
            {
              id: ToolItemAction.Disable,
              text: $epb.messages.crud.actions.disable,
              disabled: !actionPermissions.disable,
            },
          ]
        : []),
    ]
  }

  const tableData = computed((): Array<any> => {
    return data.value.results.map(result => {
      const tablePivots = {}
      let pivotsTooltip
      filtersConfig.value.forEach(config => {
        const encodedId = FiltersUtils.getEncodedId(config)
        let tablePivotValues: Array<string> | string = result.criteria.pivots
          ?.find(({ name }) => name === encodedId)
          ?.values.map(value => {
            const valueConfig = FiltersUtils.findValueConfig(config.values, { taggingId: value })
            if (!valueConfig) {
              return value
            }
            return FiltersUtils.getValueLabel(config, valueConfig, filtersMessages)
          })
        if (!tablePivotValues?.length) {
          tablePivotValues = filtersMessages.values.all.value
          if (!pivotsTooltip) {
            pivotsTooltip = {}
          }
          pivotsTooltip[config.id] = sortValuesAlphabetically(
            visibleFiltersConfig.value[config.id].values.map(
              ({ id, prettyName }) => prettyName ?? id,
            ),
          )
        }
        tablePivots[config.id] = Array.isArray(tablePivotValues)
          ? sortValuesAlphabetically(tablePivotValues)
          : tablePivotValues
      })
      const tableDataItem = DomainObjectUtils.clone(result)
      tableDataItem.criteria = {
        ...result.criteria,
        pivots: tablePivots,
        pivotsTooltip,
      } as unknown as IToolingBaseComposableDataItemCriteria
      tableDataItem.options = getDataItemOptions(tableDataItem)

      return tableDataItem
    })
  })

  return {
    ...toolingBaseComputed,
    visiblePivotColumns,
    tableData,
  }
}
